import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { isObservableArray } from 'mobx';

import { SCENARIO_TYPES as MORTGAGE_SCENARIOS } from '@ratehub/mtg-common';
import {
    Sizes,
    PROVINCE,
    formatPercentage,
    LegacyCityPropType,
} from '@ratehub/base-ui';

import PAGE_TEMPLATES from '../definitions/PageTemplates';
import MESSAGES from '../definitions/ProductTableMessages';
import { Messages as MORTGAGE_TERM_MESSAGES, MORTGAGE_TYPES, MORTGAGE_TERMS } from '../definitions/MortgageTerms';
import useMortgageRatesFilters from '../hooks/useMortgageRatesFilters';
import ProductTable from './ProductTable';


const PROVIDER = 'PROVIDER';
const RATE = 'RATE';
const TERM = 'TERM';
const TYPE = 'TYPE';

const SORT_RATE = 'rate';
const SORT_TERM = 'term';

const DEFAULT_MORTGAGE_TERM = MORTGAGE_TERMS[4];

function ProductTableMortgagesSEO({
    title,
    hideTimestamp,
    lastUpdatedDate,

    initialTerm,
    initialType,
    initialScenario,
    initialProvinceCode,
    initialCity,
    initialRates,

    columnOrder,
    sortBy,

    maxRows,

    isCentred,
    className,
}) {
    const intl = useIntl();

    const state = useMortgageRatesFilters(
        {
            term: initialTerm,
            type: initialType,
            scenario: initialScenario,
            province: initialProvinceCode,
            city: initialCity,
            rates: initialRates,
        },
    );

    const ROW_COLUMN_MAP = {
        [PROVIDER]: {
            column: {
                label: intl.formatMessage(MESSAGES.tableProvider),
                align: 'left',
            },
            cell: rate => ({
                content: rate?.provider ? rate.provider.name : '',
            }),
        },
        [RATE]: {
            column: {
                label: intl.formatMessage(MESSAGES.tableRate),
                fullRowBelowWidth: undefined,
                align: 'left',
            },
            cell: rate => ({
                // eslint-disable-next-line react/jsx-one-expression-per-line
                content: <strong>{formatPercentage(rate.value, intl)}</strong>,
            }),
        },
        [TERM]: {
            column: {
                label: intl.formatMessage(MESSAGES.tableTerm),
                align: 'left',
            },
            cell: rate => ({
                content: rate.scenario === MORTGAGE_SCENARIOS.HELOC ? '--' : intl.formatMessage(MESSAGES.termByYear, { value: rate.term / 12 }),
            }),
        },
        [TYPE]: {
            column: {
                label: intl.formatMessage(MESSAGES.tableType),
                align: 'left',
            },
            cell: rate => ({
                content: rate.scenario === MORTGAGE_SCENARIOS.HELOC ? '--' : intl.formatMessage(MORTGAGE_TERM_MESSAGES[rate.type]),
            }),
        },
    };

    const columns = columnOrder.map(colName => ROW_COLUMN_MAP[colName] ? ROW_COLUMN_MAP[colName].column : {});

    const rows = Array.isArray(state.rates) || isObservableArray(state.rates)
        ? state.rates
            .sort(sortBy === SORT_RATE
                ? (rate1, rate2) => rate1.value - rate2.value
                : (rate1, rate2) => rate1.term - rate2.term,
            )
            .map(rate => ({
                analyticsObject: rate,
                htmlCellContents: columnOrder.map(colName => ROW_COLUMN_MAP[colName]
                    ? ROW_COLUMN_MAP[colName].cell(rate)
                    : {},
                ),
            }))
        : [];

    return (
        <MyContainer className={className}>
            <MyTableContainer
                isCentred={isCentred}
            >
                <ProductTable
                    title={title}
                    hideTimestamp={hideTimestamp}
                    lastUpdatedDate={lastUpdatedDate}

                    columns={columns}
                    rows={rows}

                    maxRowsExpanded={maxRows}

                    initialProvinceCode={initialProvinceCode}
                    initialCity={initialCity}

                    filters={state.filters}
                    showSpinner={state.shouldShowSpinner}
                    rates={state.rates}

                    isAlwaysExpanded
                />
            </MyTableContainer>
        </MyContainer>
    );
}

ProductTableMortgagesSEO.propTypes = {
    title: PropTypes.string.isRequired,
    hideTimestamp: PropTypes.bool,
    lastUpdatedDate: PropTypes.string,

    initialTerm: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    initialType: PropTypes.string,
    initialScenario: PropTypes.string,
    initialProvinceCode: PropTypes.string,
    initialCity: LegacyCityPropType,
    initialRates: PropTypes.arrayOf(PropTypes.object).isRequired,

    columnOrder: PropTypes.arrayOf(PropTypes.string),
    sortBy: PropTypes.oneOf([
        SORT_RATE,
        SORT_TERM,
    ]),

    maxRows: PropTypes.number,

    isCentred: PropTypes.bool,
    className: PropTypes.string,
};

ProductTableMortgagesSEO.defaultProps = {
    hideTimestamp: false,
    lastUpdatedDate: undefined,

    initialTerm: DEFAULT_MORTGAGE_TERM,
    initialType: MORTGAGE_TYPES.FIXED,
    initialScenario: MORTGAGE_SCENARIOS.PURCHASE,
    initialProvinceCode: PROVINCE.ONTARIO,
    initialCity: undefined,

    columnOrder: [ RATE, TERM, TYPE, PROVIDER ],
    sortBy: SORT_RATE,

    maxRows: 10,

    isCentred: false,
    className: undefined,
};

const TABLE_MAX_WIDTH = '800px';

const MyContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;

    margin-left: -${Sizes.SPACING.ONE_AND_A_HALF};
    margin-right: -${Sizes.SPACING.ONE_AND_A_HALF};
`;

const MyTableContainer = styled.div`
    flex-grow: 1;

    margin-left: ${props => props.isCentred ? 'auto' : Sizes.SPACING.ONE_AND_A_HALF};
    margin-right: ${props => props.isCentred ? 'auto' : Sizes.SPACING.ONE_AND_A_HALF};

    max-width: ${props => props.isCentred ? TABLE_MAX_WIDTH : 'none'};
`;

ProductTableMortgagesSEO.blockKey = 'rh/mortgage-rate-table-seo';
ProductTableMortgagesSEO.requiresLayoutRow = true;
ProductTableMortgagesSEO.supportedPageTypes = [ PAGE_TEMPLATES.mortgages ];

export default ProductTableMortgagesSEO;
